import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Spinner, Alert, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api/axios';
import { Endpoints } from '../../api/Endpoints';
import { AuthContext } from '../../context/AuthContext';

function ForcePasswordChange() {
  const { t } = useTranslation();
  const { user, login, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const currentVersion = import.meta.env.PACKAGE_VERSION || '1.0.4';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError(t('min_6_chars_error', 'La nueva contraseña debe tener al menos 6 caracteres.'));
      return;
    }

    if (newPassword !== newPasswordConfirmation) {
      setError(t('password_mismatch_error', 'La confirmación de la contraseña no coincide.'));
      return;
    }

    setLoading(true);

    try {

      const response = await axiosInstance.post(Endpoints.UPDATE_INITIAL_PASSWORD, {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: newPasswordConfirmation
      });

      if (response.data && response.data.success) {
        setSuccess(t('password_updated_success_redirect', '¡Contraseña actualizada exitosamente! Redirigiendo al sistema...'));
        
        // Actualizar el estado de sesión del usuario desbloqueando su acceso
        const updatedUser = response.data.data?.user || { 
          ...user, 
          must_change_password: false, 
          requires_password_change: false 
        };
        const token = localStorage.getItem('rutex_auth_token');
        login(updatedUser, token);

        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        setError(response.data?.message || t('error_updating_password', 'Error al actualizar la contraseña.'));
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError(t('server_connection_error', 'Ocurrió un error de conexión al actualizar la contraseña.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex align-items-stretch auth h-100 w-100 m-0 p-0" style={{ backgroundColor: '#ffffff', minHeight: '100vh', width: '100%' }}>
      <div className="row flex-grow w-100 mx-0" style={{ minHeight: '100vh' }}>
        
        {/* PANEL IZQUIERDO: FORMULARIO ESTILO RUTEX */}
        <div className="col-12 col-lg-6 d-flex align-items-center justify-content-center bg-white shadow-lg z-1 p-4 p-sm-5">
          <div className="text-start w-100" style={{ maxWidth: '440px' }}>
            
            {/* LOGO OFICIAL RUTEX */}
            <div className="brand-logo mb-4 text-center text-sm-start">
              <img 
                src="/images/rutex-logo.svg" 
                alt="RUTEX Logo" 
                style={{ height: '60px', width: 'auto', objectFit: 'contain' }} 
                onError={(e) => { e.target.onerror = null; e.target.src = '/images/logo.svg'; }}
              />
            </div>

            <h3 className="fw-bold mb-1" style={{ color: '#00024f', letterSpacing: '-0.5px' }}>
              {t('mandatory_password_update', 'Actualización de Contraseña Obligatoria')}
            </h3>
            <p className="text-muted mb-4 small" style={{ fontSize: '0.92rem' }}>
              {t('mandatory_password_update_desc', 'Por motivos de seguridad, debes cambiar tu clave temporal por una nueva contraseña definitiva antes de ingresar.')}
            </p>

            {/* NOTIFICACIÓN DE ÉXITO */}
            {success && (
              <Alert 
                variant="success" 
                dismissible 
                onClose={() => setSuccess('')} 
                className="mb-4 border-0 fw-bold d-flex align-items-center shadow-sm"
                style={{ borderRadius: '12px', backgroundColor: '#d1fae5', color: '#065f46', fontSize: '0.9rem' }}
              >
                <i className="mdi mdi-check-circle me-2" style={{ fontSize: '1.4rem' }}></i>
                <span>{success}</span>
              </Alert>
            )}

            {/* NOTIFICACIÓN DE ERROR */}
            {error && (
              <Alert 
                variant="danger" 
                dismissible 
                onClose={() => setError('')} 
                className="mb-4 border-0 fw-bold d-flex align-items-center shadow-sm"
                style={{ borderRadius: '12px', backgroundColor: '#fee2e2', color: '#991b1b', fontSize: '0.9rem' }}
              >
                <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.4rem' }}></i>
                <span>{error}</span>
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              
              {/* CAMPO: CONTRASEÑA TEMPORAL ACTUAL */}
              <Form.Group className="mb-3">
                <Form.Label className="text-dark small fw-bold mb-1">
                  {t('current_temp_password', 'Contraseña Temporal Actual')} *
                </Form.Label>
                <div className="login-input-wrapper">
                  <span className="login-input-icon">
                    <i className="mdi mdi-key-variant"></i>
                  </span>
                  <input 
                    type={showCurrentPassword ? "text" : "password"} 
                    className="login-input-control"
                    placeholder={t('enter_temp_password_received', 'Ingresa la clave temporal recibida')} 
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="login-input-toggle"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    title={showCurrentPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    <i className={showCurrentPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"}></i>
                  </button>
                </div>
              </Form.Group>

              {/* CAMPO: NUEVA CONTRASEÑA DEFINITIVA */}
              <Form.Group className="mb-3">
                <Form.Label className="text-dark small fw-bold mb-1">
                  {t('new_definitive_password', 'Nueva Contraseña Definitiva')} *
                </Form.Label>
                <div className="login-input-wrapper">
                  <span className="login-input-icon">
                    <i className="mdi mdi-lock-outline"></i>
                  </span>
                  <input 
                    type={showNewPassword ? "text" : "password"} 
                    className="login-input-control"
                    placeholder={t('min_6_chars', 'Mínimo 6 caracteres')} 
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="login-input-toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    title={showNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    <i className={showNewPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"}></i>
                  </button>
                </div>
              </Form.Group>

              {/* CAMPO: CONFIRMAR NUEVA CONTRASEÑA */}
              <Form.Group className="mb-4">
                <Form.Label className="text-dark small fw-bold mb-1">
                  {t('confirm_new_password_label', 'Confirmar Nueva Contraseña')} *
                </Form.Label>
                <div className="login-input-wrapper">
                  <span className="login-input-icon">
                    <i className="mdi mdi-lock-check"></i>
                  </span>
                  <input 
                    type={showConfirmPassword ? "text" : "password"} 
                    className="login-input-control"
                    placeholder={t('repeat_new_password', 'Repite la nueva contraseña')} 
                    value={newPasswordConfirmation}
                    onChange={(e) => setNewPasswordConfirmation(e.target.value)}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="login-input-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    title={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    <i className={showConfirmPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"}></i>
                  </button>
                </div>
              </Form.Group>

              {/* BOTÓN DE ACCIÓN PRINCIPAL */}
              <div className="d-grid gap-2 mb-3">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="py-3 fw-bold text-white shadow"
                  style={{ 
                    borderRadius: '12px', 
                    background: 'linear-gradient(135deg, #183eeb 0%, #00024f 100%)', 
                    border: 'none',
                    fontSize: '1rem',
                    letterSpacing: '0.5px'
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('updating', 'Actualizando...')}
                    </>
                  ) : (
                    t('btn_update_password_and_enter', 'ACTUALIZAR CONTRASEÑA E INGRESAR')
                  )}
                </Button>
              </div>

              {/* CANCELAR Y CERRAR SESIÓN */}
              <div className="text-center mt-3">
                <button 
                  type="button" 
                  className="btn btn-link text-muted text-decoration-none small d-inline-flex align-items-center"
                  onClick={logout}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="mdi mdi-logout me-1" style={{ fontSize: '1.1rem' }}></i> 
                  <span>{t('btn_cancel_and_logout', 'Cancelar y Cerrar Sesión')}</span>
                </button>
              </div>
            </Form>

            {/* PIE DE PÁGINA CON VERSIÓN */}
            <div className="mt-4 pt-3 text-center border-top">
              <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>
                {t('system_version_title', { version: currentVersion, defaultValue: `Sistema de Gestión Comercial v${currentVersion} por Alpinca y Lácteos Danny` })}
              </small>
            </div>
          </div>
        </div>

        {/* PANEL DERECHO: BRANDING VISUAL RUTEX (Idéntico a Login) */}
        <div className="col-lg-6 d-none d-lg-flex flex-column justify-content-between p-5 m-0" style={{ background: 'linear-gradient(135deg, #00024f 0%, #183eeb 100%)', color: '#ffffff', minHeight: '100vh' }}>
          <div className="d-flex align-items-center">
            <img 
              src="/images/rutex-simbolo.svg" 
              alt="RUTEX Symbol" 
              style={{ height: '40px', filter: 'brightness(0) invert(1)' }} 
              onError={(e) => { e.target.style.display = 'none'; }} 
            />
            <span className="ms-2 fw-bold h4 mb-0 text-white" style={{ letterSpacing: '2px' }}>RUTEX</span>
          </div>
          
          <div className="my-auto py-5 px-3">
            <h2 className="fw-bold mb-4" style={{ color: '#ffffff', lineHeight: 1.35, fontSize: '1.7rem' }}>
              {t('branding_quote_title', 'Rutex no se trata solo de trazar trayectos en un mapa, sino de optimizar el intercambio de valor. Nace de la eficiencia en el movimiento.')}
            </h2>
            <p className="text-white-50" style={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t('branding_quote_subtitle', 'La Ruta no solo mueve productos, mueve soluciones. El sufijo "Ex" es sumamente potente porque no tiene un solo significado, sino que evoca expansión y experiencia.')}
            </p>
          </div>

          <div className="small text-white-50 border-top border-secondary pt-3">
            &copy; {new Date().getFullYear()} RUTEX System. {t('all_rights_reserved', 'Todos los derechos reservados.')} | {t('system_version_title', { version: currentVersion, defaultValue: `Sistema de Gestión Comercial v${currentVersion} por Alpinca y Lácteos Danny` })}
          </div>
        </div>

      </div>
    </div>
  );
}

export default ForcePasswordChange;
